import React from 'react';
import { Users, Loader2, Sparkles } from 'lucide-react';
import { Property } from '../types/property.types';
import { usePotentialMatches } from '@hooks/usePotentialMatches';
import { calculateTenantMatchScore } from '../utils/propertyMatching';

interface PropertyMatchSuggestionsProps {
  property: Property;
}

const PropertyMatchSuggestions: React.FC<PropertyMatchSuggestionsProps> = ({ property }) => {
  const { potentialMatches, loading } = usePotentialMatches({ property, maxResults: 10 });

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-2 text-sm text-text-secondary">
        <Loader2 className="w-4 h-4 text-primary-300 animate-spin" />
        Finding potential matches...
      </div>
    );
  }

  if (!potentialMatches || potentialMatches.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 text-sm text-text-secondary flex items-center gap-2">
        <Users className="w-4 h-4 text-gray-400" />
        No tenant pairs found yet for this property.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center shadow-sm">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-text-primary">Potential Tenant Pairs</h3>
          <p className="text-xs text-text-secondary">Based on budget, city, and compatibility</p>
        </div>
      </div>

      <div className="space-y-2">
        {potentialMatches.map((match) => {
          const score = calculateTenantMatchScore(property, match);
          return (
            <div
              key={match.id}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm flex items-center justify-between"
            >
              <div>
                <p className="text-sm font-semibold text-text-primary">
                  Matched tenant pair
                </p>
                <p className="text-xs text-text-secondary">
                  {match.compatibilityScore}% compatible with each other
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-primary-300">{score}% match</p>
                {match.budgetOverlap && (
                  <p className="text-[11px] text-text-muted">
                    Budget: R{match.budgetOverlap.min.toLocaleString()} - R{match.budgetOverlap.max.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyMatchSuggestions;
